/**
 * Die Regelhilfe.
 *
 * Ein Brettspiel bringt eine Anleitung mit; ein Programm muss sie mitliefern. Diese
 * Tafel erklaert den Ablauf, die Feldarten mit genau den Symbolen, die auch auf dem
 * Brett liegen, den Bauwuerfel, die Eingangsregel und das Rechnen - und zeigt am Ende
 * alle acht Besitzkarten als Uebersicht.
 *
 * Der Text steht hier und nicht im Wiki, weil er im Spiel gebraucht wird. Die
 * ausfuehrliche Fassung mit Quellen liegt in wiki/FuenfSterne/topics/regelwerk.md.
 */
import { HOTELS, HOTEL_IDS, stufen } from '../core/data/hotels'
import { FELDER } from '../core/data/spielplan'
import { feldsymbolBild, type Feldsymbol } from '../view/texturen'
import { geld } from './beschriftung'

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  klasse?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag)
  if (klasse) n.className = klasse
  if (text !== undefined) n.textContent = text
  return n
}

const FELDER_ERKLAERT: readonly (readonly [Feldsymbol, string, string])[] = [
  [
    'kauf',
    'Kauf-Feld',
    'Eines der Grundstücke kaufen, an die dieses Feld grenzt. Höchstens eine ' +
      'Besitzkarte pro Zug. Gehört das Grundstück schon jemandem, ist aber unbebaut, ' +
      'darfst du es ihm zum halben Preis abnehmen.',
  ],
  [
    'bau',
    'Baugenehmigung',
    'Ansagen, wo und wie viel gebaut werden soll, dann den Bauwürfel werfen. Mit einer ' +
      'Genehmigung darfst du mehrere Gebäude errichten - aber nur auf einem Grundstück ' +
      'und nur in der Reihenfolge der Besitzkarte.',
  ],
  [
    'eingang-gratis',
    'Ein Eingang kostenlos',
    'Ein Eingang geschenkt, zusätzlich zu allem, was du am Rathaus gekauft hast.',
  ],
  [
    'bauphase-gratis',
    'Eine Bauphase kostenlos',
    'Ein Hauptgebäude auf einem freien eigenen Grundstück, oder ein weiteres Gebäude ' +
      'für ein Hotel im Ausbau - ohne Genehmigung, ohne Kosten.',
  ],
  ['start', 'Parkplatz', 'Startfeld aller Autos. Das einzige Feld, auf dem mehrere Autos stehen dürfen.'],
]

const BAUWUERFEL_ERKLAERT: readonly (readonly [string, string, string])[] = [
  ['#3f9a5c', '', 'Genehmigt. Den Baupreis der Besitzkarte zahlen. Drei der sechs Seiten.'],
  ['#c2503a', '', 'Abgelehnt. Warten bis zum nächsten Bau-Feld.'],
  ['#e9e4d7', 'H', 'Genehmigt und kostenlos.'],
  ['#e9e4d7', '2', 'Genehmigt, aber der doppelte Baupreis.'],
]

function abschnitt(titel: string): HTMLElement {
  const a = el('section', 'hilfe-abschnitt')
  a.append(el('h3', undefined, titel))
  return a
}

function absatz(text: string): HTMLElement {
  return el('p', undefined, text)
}

/** Baut die Regelhilfe. Sie wird einmal erzeugt und danach nur ein- und ausgeblendet. */
export function baueRegelhilfe(): { wurzel: HTMLElement; umschalten: () => void } {
  const wurzel = el('div', 'hilfe')
  wurzel.hidden = true
  wurzel.setAttribute('role', 'dialog')
  wurzel.setAttribute('aria-label', 'Spielregeln')

  const blatt = el('div', 'hilfe-blatt')
  const kopf = el('header', 'hilfe-kopf')
  kopf.append(el('h2', undefined, 'Spielregeln'))
  const zu = el('button', 'hilfe-zu', '×')
  zu.setAttribute('aria-label', 'Regeln schließen')
  kopf.append(zu)
  blatt.append(kopf)

  const inhalt = el('div', 'hilfe-inhalt')
  blatt.append(inhalt)
  wurzel.append(blatt)

  // --- Ziel und Ablauf ---
  const ziel = abschnitt('Worum es geht')
  ziel.append(
    absatz(
      'Grundstücke kaufen, Baugenehmigungen erwürfeln, Hotels hochziehen und Eingänge ' +
        'an die Straße setzen. Wer an einem fremden Eingang hält, übernachtet dort und ' +
        'zahlt. Wer kein Geld, kein Grundstück und kein Hotel mehr hat, scheidet aus. ' +
        'Wer zuletzt übrig bleibt, gewinnt.',
    ),
  )
  const schritte = el('ol', 'hilfe-liste')
  for (const t of [
    'Grundstück kaufen',
    'Baugenehmigung erwürfeln',
    'Hauptgebäude errichten, danach beliebig weitere Gebäude',
    'Eingänge kaufen und zur Straße hin aufstellen',
    'Freizeitpark anlegen, sobald das Hotel komplett ist',
  ]) {
    schritte.append(el('li', undefined, t))
  }
  ziel.append(el('p', 'hilfe-klein', 'In dieser Reihenfolge entsteht ein Hotel:'), schritte)
  inhalt.append(ziel)

  // --- Zug ---
  const zug = abschnitt('Ein Zug')
  zug.append(
    absatz(
      'Würfeln und so viele Felder vorwärts ziehen. Zwei Autos dürfen nicht auf ' +
        'demselben Feld stehen; ist das Zielfeld besetzt, geht es auf das nächste ' +
        'freie Feld davor zurück. Wer eine Sechs wirft, ist danach noch einmal an der ' +
        'Reihe - darf aber auch verzichten.',
    ),
  )
  zug.append(
    absatz(
      'Die Leertaste löst jeweils die naheliegende Aktion aus: würfeln, bezahlen, ' +
        'ein Geschenk annehmen.',
    ),
  )
  inhalt.append(zug)

  // --- Felder ---
  const felder = abschnitt('Die Felder')
  const anzahl = (art: string) => FELDER.filter((f) => f.art === art).length
  for (const [symbol, name, text] of FELDER_ERKLAERT) {
    const zeile = el('div', 'hilfe-zeile')
    const bild = el('img', 'hilfe-symbol')
    bild.src = feldsymbolBild(symbol)
    bild.alt = ''
    const rechts = el('div')
    rechts.append(
      el('div', 'hilfe-name', `${name} · ${anzahl(symbol)}×`),
      el('div', 'hilfe-text', text),
    )
    zeile.append(bild, rechts)
    felder.append(zeile)
  }
  inhalt.append(felder)

  // --- Linien ---
  const linien = abschnitt('Bank und Rathaus')
  linien.append(
    absatz(
      'Wer die Linie an der Bank überquert, bekommt 2.000 €. Sind von ursprünglich ' +
        'drei oder vier Spielern nur noch zwei übrig, zahlt die Bank nicht mehr - dann ' +
        'lebt man nur noch von Übernachtungen.',
    ),
  )
  linien.append(
    absatz(
      'Wer die gelbe Linie am Rathaus überquert, darf für jedes eigene Hotel mit ' +
        'Hauptgebäude einen Eingang kaufen.',
    ),
  )
  inhalt.append(linien)

  // --- Bauwürfel ---
  const wuerfel = abschnitt('Der Bauwürfel')
  for (const [farbe, zeichen, text] of BAUWUERFEL_ERKLAERT) {
    const zeile = el('div', 'hilfe-zeile')
    const kasten = el('div', 'hilfe-wuerfel', zeichen)
    kasten.style.background = farbe
    kasten.style.color = farbe === '#e9e4d7' ? '#22262e' : '#ffffff'
    zeile.append(kasten, el('div', 'hilfe-text', text))
    wuerfel.append(zeile)
  }
  wuerfel.append(
    absatz(
      'Wer eine Genehmigung bekommt, muss bauen - notfalls, indem er Besitz ' +
        'versteigert. Auf einem frisch gekauften Grundstück wird erst ab der nächsten ' +
        'Runde gebaut.',
    ),
  )
  inhalt.append(wuerfel)

  // --- Eingänge ---
  const eingaenge = abschnitt('Eingänge - der taktische Kern')
  eingaenge.append(
    absatz(
      'Ein Eingang steht an einem Straßenfeld, das an dein Grundstück grenzt. ' +
        'An einem Feld darf immer nur ein einziger Eingang stehen, egal wem er gehört. ' +
        'Wer zuerst da ist, blockiert die Gegenseite dauerhaft.',
    ),
  )
  eingaenge.append(
    absatz(
      'Deshalb sind die Felder wertvoll, die an zwei Grundstücke grenzen: dort nimmt ' +
        'ein Eingang dem Nachbarn seine Kundschaft weg.',
    ),
  )
  inhalt.append(eingaenge)

  // --- Übernachten ---
  const nacht = abschnitt('Übernachten')
  nacht.append(
    absatz(
      'Endet dein Zug auf einem Feld mit fremdem Eingang, würfelst du die Zahl der ' +
        'Nächte. Es kostet Nächte × Nachtsatz. Der Nachtsatz hängt an der Ausbaustufe ' +
        'des Hotels, nicht allein an den Sternen - zwei Stufen können dieselbe ' +
        'Sternezahl und trotzdem verschiedene Preise haben.',
    ),
  )
  nacht.append(
    absatz(
      'Wer nicht bar zahlen kann, muss versteigern: ein bebautes Hotel geht immer als ' +
        'Ganzes weg, ohne Mindestpreis. Bietet niemand, wird es abgerissen und die ' +
        'Besitzkarte ist wieder frei.',
    ),
  )
  inhalt.append(nacht)

  // --- Besitzkarten ---
  const karten = abschnitt('Alle acht Hotels')
  const tabelle = el('table', 'hilfe-tabelle')
  const kopfzeile = el('tr')
  for (const t of ['Hotel', 'Grundstück', 'Eingang', 'Gebäude', 'höchster Nachtsatz']) {
    kopfzeile.append(el('th', undefined, t))
  }
  tabelle.append(kopfzeile)
  for (const id of HOTEL_IDS) {
    const h = HOTELS[id]
    const alle = stufen(h)
    const zeile = el('tr')
    const name = el('td')
    const punkt = el('span', 'hilfe-punkt')
    punkt.style.background = h.farbe
    name.append(punkt, document.createTextNode(h.name))
    zeile.append(
      name,
      el('td', undefined, geld(h.grundstueck)),
      el('td', undefined, geld(h.eingang)),
      el('td', undefined, String(h.gebaeude.length)),
      el('td', undefined, `${geld(alle[alle.length - 1]!.nachtsatz)}/Nacht`),
    )
    tabelle.append(zeile)
  }
  karten.append(tabelle)
  karten.append(
    el(
      'p',
      'hilfe-klein',
      'Die vollständige Preistabelle eines Hotels steht in der Seitenleiste, sobald ' +
        'es zur Entscheidung ansteht.',
    ),
  )
  inhalt.append(karten)

  function umschalten(): void {
    wurzel.hidden = !wurzel.hidden
    if (!wurzel.hidden) inhalt.scrollTop = 0
  }

  zu.addEventListener('click', umschalten)
  wurzel.addEventListener('click', (e) => {
    if (e.target === wurzel) umschalten()
  })

  return { wurzel, umschalten }
}
